import React, { useCallback, useMemo, useState } from 'react';
import { useLogsData } from '../../../../../../Hooks/TT_ELD/Logs';
import moment from 'moment';
import { Button, DatePicker, Drawer, Grid, Select, Space } from 'antd';
import LogsTable from './LogsTable';
import { logs } from '../../../../../../API/TT_ELD/logs';
import { useParams } from 'react-router-dom';
import dayjs from 'dayjs';
import { useUserData } from '../../../../../../Hooks/TT_ELD/Users';
import { sendEvent } from '../../../../../../Utils/analytics';
import OpenNewDay from './OpenNewDay';
import { FilterOutlined } from '@ant-design/icons';

const { useBreakpoint } = Grid;

interface Params {
  id: string | undefined;
  [key: string]: string | undefined;
}

type Data = {
  data?: { data: Array<any>; count: number };
  isLoading?: boolean;
  refetch?: any;
  isFetching?: any;
};

const recordOptions = [
  { value: 0, label: '0 - (Original)' },
  { value: 1, label: '1 - (Active)' },
  { value: 2, label: '2 - (Inactive changed)' },
  { value: 3, label: '3 - (Inactive requested)' },
  { value: 5, label: '5 - (Inactive offline)' },
  { value: 20, label: '20 - (Inactive changed)' },
  { value: 40, label: '40 - (Inactive exchanged)' },
  { value: 70, label: '70 - (Cron malfunction)' },
  { value: 74, label: '74 - (App malfunction)' },
  { value: 150, label: '150 - (Offline)' },
];

function Logs({ calendar, setCalendar, recordStatus, setRecordStatus }: any) {
  const screens = useBreakpoint();
  const isMobile = !screens.md;

  const { id } = useParams<Params>();
  const data2 = useUserData(id);

  const [filtersOpen, setFiltersOpen] = useState(false);

  const [openDay, setOpenDay] = useState(false);
  const [todayDate, setTodayDate] = useState<string>(
    moment.utc(moment().format('YYYY-MM-DD'), 'YYYY-MM-DD').format('DD-MM-YYYY'),
  );

  const lastOpenNewDayRequestDate = data2?.data?.config?.lastOpenNewDayRequestDate || null;

  const { data, refetch, isLoading, isFetching }: Data = useLogsData(
    id,
    calendar ? calendar : todayDate,
    recordStatus,
  );

  const dateFormat = 'DD-MM-YYYY';
  const dateObj = dayjs(calendar, dateFormat);
  const defaultValue: any = dayjs();

  const [isModalVisible, setIsModalVisible] = useState(false);
  const [open, setOpen] = useState(false);

  let array: any = data;
  const reversedArray: any[] = [];
  for (let i = array?.length - 1; i >= 0; i--) reversedArray.push(array[i]);

  const showModal = () => setOpenDay(true);

  const changeDate = useCallback((date: string) => {
    setTodayDate(date);
  }, []);

  const datePicker = (date: any, dateString: any) => {
    changeDate(moment.utc(dateString, 'DD-MM-YYYY').format('DD-MM-YYYY'));
    setCalendar(dateString);
    sendEvent('Logs Calendar', 'Logs Calendar', ` Logs tab Calendar `, 1);
  };

  const onSubmit = async () => {
    await logs.cleanCache(id, todayDate);
    sendEvent(
      'Logs tab Clean Cache Button',
      'Logs tab Clean Cache Button',
      ` Logs tab Clean Cache Button`,
      1,
    );
    refetch();
  };

  const handleChange = (value: any) => {
    setRecordStatus(value);
    sendEvent('Logs Tab Record_Status', 'Logs Tab Record_Status', 'Logs Record_Status', 1);
  };

  const filtersUI = useMemo(
    () => (
      <div style={{ display: 'flex', flexDirection: 'column', gap: 10, width: '100%' }}>
        <DatePicker
          style={{ width: '100%' }}
          value={calendar ? dateObj : defaultValue}
          format="DD-MM-YYYY"
          disabledDate={(current) => {
            const customDate = moment().add(1, 'd').format('DD-MM-YYYY');
            return current && current > moment(customDate, 'DD-MM-YYYY');
          }}
          onChange={datePicker}
        />

        <Select
          showSearch
          placeholder="Select a status"
          optionFilterProp="children"
          filterOption={(input, option: any) =>
            (option?.label ?? '').toLowerCase().includes(input.toLowerCase())
          }
          value={Number(recordStatus)}
          onChange={handleChange}
          style={{ width: '100%' }}
          options={recordOptions.map((o) => ({ value: o.value, label: o.label }))}
        />

        <div style={{ display: 'flex', gap: 3 }}>
          {Number(recordStatus) === 1 && (
            <Button block type="primary" onClick={showModal}>
              Open New Day
            </Button>
          )}
          {openDay && (
            <OpenNewDay
              open={openDay}
              setOpen={setOpenDay}
              refetch={refetch}
              driverId={id}
              lastOpenNewDayRequestDate={lastOpenNewDayRequestDate}
            />
          )}
          <Button
            block
            type="primary"
            onClick={onSubmit}
            style={{ backgroundColor: 'rgb(89, 90, 212)', borderColor: 'rgb(89, 90, 212)' }}
          >
            Clean Cache
          </Button>
        </div>

        {isMobile && (
          <Space style={{ width: '100%' }} size={10}>
            <Button block onClick={() => setFiltersOpen(false)}>
              Close
            </Button>
            <Button
              block
              type="primary"
              onClick={() => {
                refetch();
                setFiltersOpen(false);
              }}
            >
              Apply & Refresh
            </Button>
          </Space>
        )}
      </div>
    ),
    [calendar, dateObj, defaultValue, recordStatus, todayDate, isMobile],
  );

  return (
    <div>
      {/* Toolbar */}
      {!isMobile ? (
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: 12,
            marginBottom: 10,
          }}
        >
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 5,
            }}
          >
            <DatePicker
              style={{ width: 160 }}
              value={calendar ? dateObj : defaultValue}
              format="DD-MM-YYYY"
              disabledDate={(current) => {
                const customDate = moment().add(1, 'd').format('DD-MM-YYYY');
                return current && current > moment(customDate, 'DD-MM-YYYY');
              }}
              onChange={datePicker}
            />
            <Select
              showSearch
              placeholder="Select a status"
              optionFilterProp="children"
              filterOption={(input, option: any) =>
                (option?.label ?? '').toLowerCase().includes(input.toLowerCase())
              }
              value={Number(recordStatus)}
              onChange={handleChange}
              style={{ width: 200 }}
              options={recordOptions.map((o) => ({ value: o.value, label: o.label }))}
            />

            {Number(recordStatus) === 1 && (
              <Button type="primary" onClick={showModal}>
                Open New Day
              </Button>
            )}

            <Button
              onClick={onSubmit}
              type="primary"
              style={{ backgroundColor: 'rgb(89, 90, 212)', borderColor: 'rgb(89, 90, 212)' }}
            >
              Clean Cache
            </Button>
          </div>

          {/* Desktop actions right */}
          <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            <Button size="large" onClick={refetch}>
              Refresh
            </Button>

            {isModalVisible && (
              <Button size="large" type="primary" onClick={() => setOpen(!open)}>
                Bulk Edit
              </Button>
            )}
          </div>
        </div>
      ) : (
        // Mobile actions
        <div style={{ display: 'flex', gap: 10, marginBottom: 10 }}>
          <Button
            block
            size="large"
            icon={<FilterOutlined />}
            onClick={() => setFiltersOpen(true)}
          />
          <Button block size="large" onClick={refetch}>
            Refresh
          </Button>
        </div>
      )}

      {/* Mobile Drawer */}
      <Drawer
        title="Filters"
        placement="bottom"
        open={filtersOpen}
        onClose={() => setFiltersOpen(false)}
        height="60vh"
      >
        {filtersUI}
      </Drawer>

      {/* Modal */}
      {openDay && (
        <OpenNewDay
          open={openDay}
          setOpen={setOpenDay}
          refetch={refetch}
          driverId={id}
          lastOpenNewDayRequestDate={lastOpenNewDayRequestDate}
        />
      )}

      {/* Table */}
      <LogsTable
        logs={reversedArray}
        userId={id}
        data2={data2}
        recordStatus={recordStatus}
        isLoading={isLoading}
        isFetching={isFetching}
        refetch={refetch}
        setIsModalVisible={setIsModalVisible}
        open={open}
        setOpen={setOpen}
        toDayDate={todayDate}
      />
    </div>
  );
}

export default Logs;
